import React, { useState, useRef, useEffect, useContext } from 'react';
import cn from 'clsx';
import { arrayOf, string, shape } from 'prop-types';
import { LoggedInContext } from '@immediate_media/component-config';
import { useTranslation } from 'react-i18next';
import DropDownNav from '../DropDownNav/index.jsx';
import Link from '../../atoms/Link/index.jsx';
import MenuLink from '../../atoms/MenuLink/index.jsx';
import List from '../List/index.jsx';
import Icon from '../../atoms/Icon/index.jsx';
import Image from '../../atoms/Image/index.jsx';
import Avatar from '../Avatar/index.jsx';

/**
 * AccountNav component.
 */
var _ref = /*#__PURE__*/React.createElement(List.Item, null, /*#__PURE__*/React.createElement(Icon, {
  className: "account-nav__icon mr-xs chromatic-ignore",
  color: "inherit",
  name: "profile",
  size: 25
}));
const AccountNav = ({
  signInUrl,
  registerUrl,
  menuItems,
  className,
  label,
  avatarImage
}) => {
  const [showAccountNavDropDown, setShowAccountNavDropDown] = useState(false);
  const [showAccountNav, setShowAccountNav] = useState(false);
  const btnRef = useRef(null);
  const navId = 'account-dropdown';
  const isLoggedIn = useContext(LoggedInContext);
  const {
    t
  } = useTranslation();
  const accountLabel = label || t('molecules.accountNav.defaultLabel');

  // Hide the dropdown when clicking anywhere in the document, apart from the
  // button. If the click was a menu item we want to close and navigate, if it
  // was outside the menu we also want to close.
  const handleClick = evt => {
    if (btnRef.current && !btnRef.current.contains(evt.target)) {
      setShowAccountNavDropDown(false);
    }
  };
  useEffect(() => {
    document.addEventListener('click', handleClick, false);
    return () => {
      document.removeEventListener('click', handleClick, false);
    };
  }, []);

  // Renders the account nav in the client rather than SSR
  // to prevent unwanted caching of this component
  useEffect(() => {
    setShowAccountNav(true);
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: cn(className, 'account-nav')
  }, showAccountNav && (isLoggedIn ? /*#__PURE__*/React.createElement("div", {
    className: "account-nav__logged-in-wrapper"
  }, /*#__PURE__*/React.createElement("button", {
    className: "body-copy-small account-nav__dropdown-btn",
    ref: btnRef,
    type: "button",
    onClick: () => {
      setShowAccountNavDropDown(!showAccountNavDropDown);
    },
    "aria-haspopup": "true",
    "aria-controls": navId
  }, /*#__PURE__*/React.createElement("div", {
    className: "account-nav__avatar mr-xxs chromatic-ignore"
  }, /*#__PURE__*/React.createElement(Avatar, {
    image: avatarImage,
    noShadow: true
  })), accountLabel), /*#__PURE__*/React.createElement(DropDownNav, {
    className: 'account-nav__drop-down-nav' + (showAccountNavDropDown ? " account-nav__drop-down-nav--open" : ""),
    menuItems: menuItems,
    domId: navId,
    ariaLabel: t('molecules.accountNav.ariaLabel.account')
  })) : /*#__PURE__*/React.createElement("nav", {
    "aria-label": t('molecules.accountNav.ariaLabel.account')
  }, /*#__PURE__*/React.createElement(List, {
    className: "account-nav__list",
    horizontal: true
  }, _ref, /*#__PURE__*/React.createElement(List.Item, {
    className: "body-copy-small"
  }, /*#__PURE__*/React.createElement(Link, {
    href: signInUrl,
    className: "account-nav__link"
  }, t('molecules.accountNav.signInText'))), /*#__PURE__*/React.createElement(List.Item, {
    className: "account-nav__listitem body-copy-small"
  }, /*#__PURE__*/React.createElement(Link, {
    href: registerUrl,
    className: "account-nav__link"
  }, t('molecules.accountNav.registerText')))))));
};
AccountNav.defaultProps = {
  menuItems: [],
  className: null,
  label: '',
  avatarImage: null
};
AccountNav.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** Menu items for the logged in state */
  menuItems: arrayOf(shape(MenuLink.propTypes)),
  /** Logged in label state */
  label: string,
  /** Sign in URL */
  signInUrl: string.isRequired,
  /** Register URL */
  registerUrl: string.isRequired,
  /** Avatar image props */
  avatarImage: shape(Image.propTypes)
} : {};
export default AccountNav;